import React, { useCallback, useEffect } from 'react';
import CircleI from '@uber/base-icons/circle-i';
import useShortcut from '@uber/label-modalities/src/image/labelling/providers/hooks/use-shortcut';
import { useStyletron } from 'baseui';
import { Slider } from 'baseui/slider';
import { StatefulTooltip } from 'baseui/tooltip';
import { LabelXSmall } from 'baseui/typography';
import { useAtomValue, useSetAtom } from 'jotai';
import ToolbarButton from '../../../../common/toolbar-button';
import { TOOLBAR_BUTTON_SIZE } from '../../../../common/toolbar-button';
import { BoundingBox, Click, Fill } from '../../../icons';
import { EVENTS } from '../../../labelling-v2/atoms/label-store-dispatch/events';
import { debouncedPolygonEpsilonAtom, isSAM3PolygonTightnessSliderEnabledAtom, isSAM3PolygonToolsEnabledAtom, polygonEpsilonAtom } from '../../../labelling-v2/atoms/tool-atoms';
import { useStageAtoms } from '../../../labelling-v2/components/stage-scope/hooks';
import { POLYGON_TOOLS } from '../../constants';
import { useIsToolDisabled } from '../../providers/hooks/use-is-tool-disabled';
import { useSamForReview } from '../../providers/hooks/use-sam-for-review';
import { useSam3ForAutoDetectPolygon } from '../../providers/hooks/use-sam3-for-auto-detect-polygon';
import { handleShortcutRegistrationError } from '../../providers/utils/shortcut';
import useAnalyticsForToolBar from './analytics/useAnalyticsForToolBar';
import AutoDetectToolbarButton from './auto-detect-toolbar-button';
import PanelContainer from './panel-container';
import { getSAM3TextPrompt } from './utils';
const POLYGON_PANEL_MAX_WIDTH = 86;
const POLYGON_PANEL_MAX_WIDTH_SAM3 = 158;
const POLYGON_PANEL_MAX_WIDTH_SAM3_WITH_SLIDER = 230;
const SAM_CLICK_LABEL = 'Click on the region to begin segmentation';
const SAM_BOUNDING_BOX_LABEL = 'Draw a box around the desired area to segment object within';
const TIGHTNESS_TOOLTIP = 'Move left for more points that follow the shape closely; move right for fewer points.';
const MIN_EPSILON = 1;
const MAX_EPSILON = 10;
const BUTTON_OVERRIDES = {
  marginBottom: 0
};
const sliderOverrides = {
  TickBar: {
    style: {
      display: 'none'
    }
  },
  Thumb: {
    style: ({
      $theme
    }) => ({
      width: $theme.sizing.scale400,
      height: $theme.sizing.scale400
    })
  }
};
const PolygonPanel = () => {
  const {
    brushToolSettingsAtom,
    dispatchAtom,
    selectedAnnotationLabelAtom
  } = useStageAtoms();
  const {
    selectedTool
  } = useAtomValue(brushToolSettingsAtom);
  const isSAM3Enabled = useAtomValue(isSAM3PolygonToolsEnabledAtom);
  const isSAM3PolygonTightnessSliderEnabled = useAtomValue(isSAM3PolygonTightnessSliderEnabledAtom);
  const selectedAnnotationLabel = useAtomValue(selectedAnnotationLabelAtom);
  const selectedClassName = selectedAnnotationLabel ? getSAM3TextPrompt(selectedAnnotationLabel) : '';
  const dispatchAction = useSetAtom(dispatchAtom);
  const polygonEpsilon = useAtomValue(polygonEpsilonAtom);
  const setDebouncedPolygonEpsilon = useSetAtom(debouncedPolygonEpsilonAtom);
  const [css, theme] = useStyletron();
  const {
    sendEventToolSelected
  } = useAnalyticsForToolBar();
  const {
    registerHotKeys
  } = useShortcut();
  const {
    detect,
    submitAll,
    cancelAll,
    isDetecting,
    detectedCount,
    pendingAnnotationLabel,
    updateEpsilon
  } = useSam3ForAutoDetectPolygon();
  const {
    updateEpsilon: updateSamReviewEpsilon,
    hasPendingPolygonReview
  } = useSamForReview();
  const hasPendingReview = detectedCount > 0 || hasPendingPolygonReview;
  const isToolDisabled = useIsToolDisabled();
  const onEpsilonChange = useCallback(({
    value
  }: {
    value: number[];
  }) => {
    const epsilon = value[0];
    if (epsilon === undefined) return;
    setDebouncedPolygonEpsilon(epsilon, settledEpsilon => {
      if (detectedCount > 0) {
        updateEpsilon(settledEpsilon);
      } else {
        updateSamReviewEpsilon(settledEpsilon);
      }
    });
  }, [setDebouncedPolygonEpsilon, detectedCount, updateEpsilon, updateSamReviewEpsilon]);
  const onPolygonIconClick = useCallback(() => {
    dispatchAction({
      type: EVENTS.SET_BRUSH_TOOL_SETTINGS,
      brushToolSettings: {
        selectedTool: POLYGON_TOOLS.POLYGON,
        eraserSelected: false
      }
    });
    sendEventToolSelected({
      tool: POLYGON_TOOLS.POLYGON
    });
  }, [dispatchAction, sendEventToolSelected]);
  const onSAMClick = useCallback(() => {
    dispatchAction({
      type: EVENTS.SET_BRUSH_TOOL_SETTINGS,
      brushToolSettings: {
        selectedTool: POLYGON_TOOLS.SAM_CLICK,
        eraserSelected: false
      }
    });
    sendEventToolSelected({
      tool: POLYGON_TOOLS.SAM_CLICK
    });
  }, [dispatchAction, sendEventToolSelected]);
  const onSAMBoundingBoxClick = useCallback(() => {
    dispatchAction({
      type: EVENTS.SET_BRUSH_TOOL_SETTINGS,
      brushToolSettings: {
        selectedTool: POLYGON_TOOLS.SAM_BOUNDING_BOX,
        eraserSelected: false
      }
    });
    sendEventToolSelected({
      tool: POLYGON_TOOLS.SAM_BOUNDING_BOX
    });
  }, [dispatchAction, sendEventToolSelected]);
  const onAutoDetectSelect = useCallback(() => {
    dispatchAction({
      type: EVENTS.SET_BRUSH_TOOL_SETTINGS,
      brushToolSettings: {
        selectedTool: POLYGON_TOOLS.AUTO_DETECT,
        eraserSelected: false
      }
    });
    sendEventToolSelected({
      tool: POLYGON_TOOLS.AUTO_DETECT
    });
  }, [dispatchAction, sendEventToolSelected]);
  useEffect(() => {
    const hotKeys = [{
      keys: ['m'],
      onAction: onSAMClick
    }, {
      keys: ['p'],
      onAction: onPolygonIconClick
    }];
    if (isSAM3Enabled) {
      hotKeys.splice(1, 0, {
        keys: ['z'],
        onAction: onSAMBoundingBoxClick
      });
    }
    const {
      errors,
      removeHotKeys
    } = registerHotKeys(hotKeys);
    errors.forEach(error => handleShortcutRegistrationError(error));
    return () => {
      removeHotKeys();
    };
  }, [onPolygonIconClick, registerHotKeys, onSAMClick, onSAMBoundingBoxClick, isSAM3Enabled]);
  useEffect(() => {
    dispatchAction({
      type: EVENTS.SET_BRUSH_TOOL_SETTINGS,
      brushToolSettings: {
        selectedTool: POLYGON_TOOLS.POLYGON,
        eraserSelected: false
      }
    });
    sendEventToolSelected({
      tool: POLYGON_TOOLS.POLYGON
    });
  }, [sendEventToolSelected, dispatchAction]);
  const showEpsilonSlider = isSAM3Enabled && isSAM3PolygonTightnessSliderEnabled && hasPendingReview;
  const panelWidth = showEpsilonSlider ? POLYGON_PANEL_MAX_WIDTH_SAM3_WITH_SLIDER : isSAM3Enabled ? POLYGON_PANEL_MAX_WIDTH_SAM3 : POLYGON_PANEL_MAX_WIDTH;
  return <PanelContainer width={panelWidth} data-testid="polygon-panel">
      <div className={css({
      display: 'flex',
      justifyContent: 'flex-start',
      alignItems: 'center'
    })}>
        <ToolbarButton title="Polygon" customStyles={BUTTON_OVERRIDES} onClick={onPolygonIconClick} attributes={{
        'data-testid': 'polygon'
      }} isSelected={selectedTool === POLYGON_TOOLS.POLYGON} size={TOOLBAR_BUTTON_SIZE.SMALL} disabled={isToolDisabled}>
          <Fill fillColor={theme.colors.primary} />
        </ToolbarButton>
        <div className={css({
        height: theme.sizing.scale900,
        ...theme.borders.border200,
        margin: theme.sizing.scale0,
        marginLeft: theme.sizing.scale200
      })} />
        <ToolbarButton title={SAM_CLICK_LABEL} customStyles={BUTTON_OVERRIDES} onClick={onSAMClick} attributes={{
        'data-testid': 'sam_click'
      }} isSelected={selectedTool === POLYGON_TOOLS.SAM_CLICK} size={TOOLBAR_BUTTON_SIZE.SMALL} disabled={isToolDisabled}>
          <Click fillColor={theme.colors.primary} />
        </ToolbarButton>
        {isSAM3Enabled && <>
            <ToolbarButton title={SAM_BOUNDING_BOX_LABEL} customStyles={BUTTON_OVERRIDES} onClick={onSAMBoundingBoxClick} attributes={{
          'data-testid': 'sam_bounding_box'
        }} isSelected={selectedTool === POLYGON_TOOLS.SAM_BOUNDING_BOX} size={TOOLBAR_BUTTON_SIZE.SMALL} disabled={isToolDisabled}>
              <BoundingBox fillColor={theme.colors.primary} />
            </ToolbarButton>
            <AutoDetectToolbarButton detect={detect} submitAll={submitAll} cancelAll={cancelAll} isDetecting={isDetecting} detectedCount={detectedCount} selectedClassName={selectedClassName} pendingAnnotationLabel={pendingAnnotationLabel} isSelected={selectedTool === POLYGON_TOOLS.AUTO_DETECT} disabled={isToolDisabled} testId="auto_detect_polygon" onSelect={onAutoDetectSelect} />
          </>}
      </div>
      {showEpsilonSlider && <div className={css({
      display: 'flex',
      flexDirection: 'column',
      marginTop: theme.sizing.scale300,
      marginLeft: theme.sizing.scale300,
      marginRight: theme.sizing.scale300
    })} data-testid="polygon-epsilon-slider">
          <div className={css({
        display: 'flex',
        alignItems: 'center',
        gap: theme.sizing.scale100
      })}>
            <LabelXSmall>Tightness</LabelXSmall>
            <StatefulTooltip content={TIGHTNESS_TOOLTIP} showArrow placement="top">
              <span className={css({
            display: 'inline-flex',
            cursor: 'help'
          })}>
                <CircleI size={14} />
              </span>
            </StatefulTooltip>
          </div>
          <Slider step={0.5} value={[polygonEpsilon]} onChange={onEpsilonChange} min={MIN_EPSILON} max={MAX_EPSILON} overrides={sliderOverrides} />
        </div>}
    </PanelContainer>;
};
export default PolygonPanel;